import { makeAutoObservable, runInAction } from "mobx";
import WalletSetupHandler from './wallet-setup-handler';
import WalletService, { IWallet, IWalletEligiblePromotion, IWalletSettings, PLAN_CYCLE_ENUM, WALLET_PROMOTION_TYPE_ENUM } from "../../../../services/api/wallet-service";
import { rootContainer, TYPE } from "../../../../services/container/root";
import { getNullableChangesOrModel, isSet, toNullableNumber, toNumber } from "../../../../models/model-types-methods";
import CurrencyService from "../../../../services/currency-service";
import { IInputValitationResponse } from "../../../../hooks/input-validation-hook";
import { DATETIME_VIEW } from "../../../../components/datetime-v2/datetime-v2";
import { formatDateTimeWithFallback } from "../../../../utilities/date-utilities";
import currentPortalStore from "../../../../stores/current-portal-store";
import FailedDepositTransactionsStore from "./failed-deposit-transactions-store";
import walletBillingHistoryStore from "./wallet-billing-history-store";

export interface IWalletWithSetupChanges extends Partial<IWallet> {}

export class WalletStore {
    private _walletSetupHandler = new WalletSetupHandler();
    private _failedDepositTransactionsStore: FailedDepositTransactionsStore = new FailedDepositTransactionsStore(this);
    private _isLoading = false;
    private _isDeleting = false;
    private _isUpdating = false;
    private _isAddingPaymentMethod = false;
    private _isAddingCredits = false;
    private _model?: IWallet;
    private _settings?: IWalletSettings;
    private _eligiblePromotions?: IWalletEligiblePromotion[];
    private _changes: IWalletWithSetupChanges = {};
    private _showDisableAutoRechargeConfirmModalOpen: boolean = false;
    private _showDeletePaymentMethodModal: boolean = false;
    private _showAutoRechargeSettingsModal: boolean = false;
    private _showAddCreditsModal: boolean = false;
    private _manualDepositAmount: number | null = null;

    constructor() {
        makeAutoObservable(this, {}, { autoBind: true });
    }

    private get _walletService(): WalletService { return rootContainer.get<WalletService>(TYPE.WalletService); }
    private get _currencyService(): CurrencyService { return rootContainer.get<CurrencyService>(TYPE.CurrencyService);}

    get amount(): number { return this._model?.amount ?? 0; }
    get amountFormattedWithSymbol(): string { return this._currencyService.formatCurrency(this.amount, this.walletCurrencyCode!); }
    get last4(): number | undefined { return this._model?.current_wallet_setup?.card_last4; }
    get expiration(): string | undefined { return this._model?.current_wallet_setup?.card_expiration; }
    get brand(): string | undefined { return this._model?.current_wallet_setup?.card_brand; }
    get brandCapitalized(): string | undefined { return this.brand && this.brand[0].toUpperCase() + this.brand.slice(1); }
    get hasPaymentMethod(): boolean { return isSet(this._model?.current_wallet_setup); }
    get paymentMethodError(): string | null { return this._model?.current_wallet_setup?.payment_method_error ?? null; }
    get isLoading(): boolean { return this._isLoading; }
    get isDeleting(): boolean { return this._isDeleting; }
    get isUpdating(): boolean { return this._isUpdating; }
    get isAddingPaymentMethod(): boolean { return this._isAddingPaymentMethod; }
    get isAutoRechargeEnabled(): boolean | undefined { return this._changes.auto_recharge ?? this._model?.auto_recharge; }
    get isAddingCredits(): boolean { return this._isAddingCredits; }
    get shouldShowDisableAutoRechargeConfirmModalOpen(): boolean { return this._showDisableAutoRechargeConfirmModalOpen; }
    get shouldShowDeletePaymentMethodModal(): boolean { return this._showDeletePaymentMethodModal; }
    get shouldShowAutoRechargeSettingsModal(): boolean { return this._showAutoRechargeSettingsModal; }
    get shouldShowAddCreditsModal(): boolean { return this._showAddCreditsModal; }
    get rechargeThreshold(): number | null { 
        return getNullableChangesOrModel(this._changes.recharge_threshold, this._model?.recharge_threshold); 
    }
    get rechargeAmount(): number | null { 
        return getNullableChangesOrModel(this._changes.recharge_amount, this._model?.recharge_amount);
    }
    get manualDepositAmount(): number | null { return this._manualDepositAmount; }
    get manualDepositAmountFormattedWithSymbol(): string { return this._currencyService.formatCurrency(this.manualDepositAmount!, this.walletCurrencyCode!); }
    get walletCurrencyCode(): string | undefined { return this._model?.currency ?? this._settings?.shop_currency; }
    get walletCurrencySymbol(): string | undefined { return this._currencyService.getCurrencySymbol(this.walletCurrencyCode!); }
    get shouldShowAutoChargeNote(): boolean { return (this.rechargeThreshold !== null) ? this.amount < this.rechargeThreshold : true; }
    private get _maximumRechargeAmount(): number { return this._settings?.maximum_recharge_amount ?? 0; }
    private get _maximumDepositAmount(): number { return this._settings?.maximum_deposit_amount ?? 0; }
    private get _maximumManualDepositAmount(): number { return this._maximumDepositAmount - this.amount; }
    private get _maximumDepositAmountFormatted(): string { return this._currencyService.formatCurrency(this._maximumDepositAmount, this.walletCurrencyCode!); }
    get maximumRechargeAmountFormattedWithSymbol(): string { return this._currencyService.formatCurrency(this._maximumRechargeAmount, this.walletCurrencyCode!); }
    private get _minimumDepositAmount(): number { return Math.round(this._settings?.minimum_deposit_amount ?? 0); }
    get minimumDepositAmountFormattedWithSymbol(): string { return this._currencyService.formatCurrency(this._minimumDepositAmount, this.walletCurrencyCode!); }
    get lastRechargeWalletAfterAmount(): number | null { return this._model?.last_recharge?.wallet_after_amount ?? null; }
    get failedDepositTransactionsStore(): FailedDepositTransactionsStore { return this._failedDepositTransactionsStore; }
    get firstChargeBonusPromotion(): IWalletEligiblePromotion | null {
        return this._eligiblePromotions?.find(promotion => promotion.type === WALLET_PROMOTION_TYPE_ENUM.TYPE_FIRST_CHARGE_BONUS) ?? null;
    }
    
    get planName(): string { return this._settings?.plan?.plan_name ?? ''; }
    get planPrice(): number | undefined { return this._settings?.plan?.real_plan_price; }
    get planCycle(): string | undefined {
        const planCycle = this._settings?.plan?.plan_cycle;
        const planCycleMap: Record<PLAN_CYCLE_ENUM, string> = {
            [PLAN_CYCLE_ENUM.MONTHLY]: 'Monthly',
            [PLAN_CYCLE_ENUM.YEARLY]: 'Yearly',
            [PLAN_CYCLE_ENUM.INSTALLMENTS]: 'Installments'
        };

        return planCycle ? planCycleMap[planCycle] : undefined;
    }
    
    get walletStatusBarMaximumPoint(): number | null {
        if (!isSet(this.lastRechargeWalletAfterAmount)) {
            return null;
        }
        return this.rechargeThreshold !== null && this.rechargeThreshold > this.lastRechargeWalletAfterAmount! ? this.rechargeThreshold : this.lastRechargeWalletAfterAmount;
    }
    
    private get _lastRechargeDate(): string | null { return this._model?.last_recharge?.created_at ?? null; }
    get lastRechargeDateFormatted(): string | null {
        if (!this._lastRechargeDate) {
            return null;
        }
        const tzStore = currentPortalStore.setting?.timezoneStore;
        return formatDateTimeWithFallback(tzStore, this._lastRechargeDate, DATETIME_VIEW.DATE_ONLY);
    }

    public validateMinimumThresholdEditor(): IInputValitationResponse {
        const errors = [];
        if (this.rechargeThreshold === null) errors.push('Minimum threshold must be specified');
        else if (this.rechargeThreshold < this._minimumDepositAmount) errors.push('Minimum threshold must be greater than ' + this.minimumDepositAmountFormattedWithSymbol);
        return { isValid: errors.length === 0, messages: errors };
    }

    public validateRechargeAmountEditor(): IInputValitationResponse {
        const errors = [];
        if (this.rechargeAmount === null) errors.push('Recharge amount must be specified');
        else if (this.rechargeAmount < this.rechargeThreshold!) errors.push('Recharge amount must be greater than the minimum threshold');
        else if (this.rechargeAmount > this._maximumRechargeAmount) errors.push('Recharge amount must be less than the maximum recharge amount');
        return { isValid: errors.length === 0, messages: errors };
    }

    public validateManualDepositAmountEditor(): IInputValitationResponse {
        const errors = [];
        if (this.manualDepositAmount === null) errors.push('Amount to add must be specified');
        else if (this.manualDepositAmount < this._minimumDepositAmount) errors.push('Recharge amount must be greater than ' + this.minimumDepositAmountFormattedWithSymbol);
        else if (this.manualDepositAmount > this._maximumManualDepositAmount) errors.push(`The wallet can have a maximum of ${this._maximumDepositAmountFormatted} pre-paid balance at a time`);
        return { isValid: errors.length === 0, messages: errors };
    }
    
    public setShouldShowDisableAutoRechargeConfirmModalOpen(v: boolean) {
        this._showDisableAutoRechargeConfirmModalOpen = v;
    }

    public setShouldShowDeletePaymentMethodModal(v: boolean) {
        this._showDeletePaymentMethodModal = v;
    }

    public setShouldShowAutoRechargeSettingsModal(shouldShowAutoRechargeModal: boolean) {
        this._showAutoRechargeSettingsModal = shouldShowAutoRechargeModal;

        if (!shouldShowAutoRechargeModal) {
            this.discardChanges();
            return;
        }

        if (!isSet(this.rechargeAmount)) {
            this._changes.recharge_amount = this._minimumDepositAmount;
        }
        if (!isSet(this.rechargeThreshold)) {
            this._changes.recharge_threshold = this._minimumDepositAmount;
        }
    }

    public setShouldShowAddCreditsModal(v: boolean) {
        if (v === true && !isSet(this.manualDepositAmount)) {
            this.setManualDepositAmount(this._minimumDepositAmount);
        }
        this._showAddCreditsModal = v;
    }

    public setMinimumThreshold(v: number|null) {
        this._changes.recharge_threshold = v;
    }

    public setRechargeAmount(v: number|null) {
        this._changes.recharge_amount = v;
    }

    public setManualDepositAmount(value: number) {
        if (value >= 0) {
            this._manualDepositAmount = value;
        }
    }

    private getUpdatedModel(): IWallet {
        return { ...this._model, ...this._changes } as IWallet;
    }

    private setModel(walletModel: IWallet): void {
        if (isSet(walletModel)) {
            this._model = this._ensureValidModel(walletModel);
        }
        this._changes = {};
    }

    private discardChanges(): void {
        this._changes = {};
    }

    private setSettings(walletSettings: IWalletSettings): void {
        this._settings = this._ensureValidWalletSettings(walletSettings);
    }

    private setEligiblePromotions(eligiblePromotions: IWalletEligiblePromotion[]): void {
        this._eligiblePromotions = eligiblePromotions;
    }

    private async updateWallet() {
        try {
            this._isUpdating = true;
            const updatedModel = await this._walletService.updateWallet(this.getUpdatedModel());
            runInAction(() => {
                this.setModel(updatedModel);
                this._isUpdating = false;
            });
        } catch (e) {
            runInAction(() => {
                this._isUpdating = false;
            });
        }
    }

    private async autoDeposit() {
        try {
            this._isUpdating = true;
            const response = await this._walletService.autoDeposit(this.getUpdatedModel());
            runInAction(() => {
                this.setModel(response.wallet);
                this.setEligiblePromotions(response.eligible_promotions);
                this._isUpdating = false;

                /** refresh the wallet billing history to get the latest data */
                walletBillingHistoryStore.refresh();
            });
        } catch (e) {
            await this.failedDepositTransactionsStore.load();
            runInAction(() => {
                this._isUpdating = false;
            });
        }
    }

    public async updateWalletWithAutoDeposit() {
        this._changes.auto_recharge = true;
        await this.updateWallet();
        await this.autoDeposit();
        runInAction(() => {
            this.setShouldShowAutoRechargeSettingsModal(false);
        });
    }

    public toggleAutoRecharge(): void {
        if (this.isAutoRechargeEnabled) {
            this.setShouldShowDisableAutoRechargeConfirmModalOpen(true);
        } else {
            this.setShouldShowAutoRechargeSettingsModal(true);
        }
    }
    
    public async disableAutoRecharge() {
        this._changes.auto_recharge = false;
        await this.updateWallet();
        runInAction(() => {
            this.setShouldShowDisableAutoRechargeConfirmModalOpen(false);
        });
    }

    public async addPaymentMethodHandler(): Promise<void> {
        try {
            this._isAddingPaymentMethod = true;
            const walletModel = await this._walletSetupHandler.setupWallet(this.getUpdatedModel());
            runInAction(() => {
                this.setModel(walletModel);
                this._isAddingPaymentMethod = false;
                if (!isSet(this.rechargeAmount) || !isSet(this.rechargeThreshold)) {
                    this.setShouldShowAutoRechargeSettingsModal(true);
                }
            });
        } 
        catch (e) {
            runInAction(() => {
                this._isAddingPaymentMethod = false;
            });
        }
    }

    public async manualDepositHandler(): Promise<void> {
        try {
            this._isAddingCredits = true;
            const response = await this._walletService.manualDeposit(this.manualDepositAmount!);
            runInAction(() => {
                this.setModel(response.wallet);
                this.setEligiblePromotions(response.eligible_promotions);
                this._isAddingCredits = false;                
                this.setShouldShowAddCreditsModal(false);

                /** refresh the wallet billing history to get the latest data */
                walletBillingHistoryStore.refresh();
            });
        }
        catch (e) {
            await this.failedDepositTransactionsStore.load();
            runInAction(() => {
                this._isAddingCredits = false;
            });
        }
    }

    public async deletePaymentMethodHandler(): Promise<void> {
        try {
            this._isDeleting = true;
            const walletSetupId = this._model?.current_wallet_setup?.id;
            if (!walletSetupId) return;

            const walletWithSetupModel = await this._walletService.deletePaymentMethod(walletSetupId);
            runInAction(() => {
                this.setModel(walletWithSetupModel);
                this._isDeleting = false;
                this.setShouldShowDeletePaymentMethodModal(false);
            });
        }
        catch (e) {
            runInAction(() => {
                this._isDeleting = false;
                this.setShouldShowDeletePaymentMethodModal(false);
            });
        }
    }

    public async load(): Promise<void> {
        try {
            this._isLoading = true;
            const walletWithSettings = await this._walletService.fetchWallet();
            this.failedDepositTransactionsStore.load();
            runInAction(() => {
                this.setModel(walletWithSettings.wallet);
                this.setSettings(walletWithSettings.settings);
                this.setEligiblePromotions(walletWithSettings.eligible_promotions);
                this._isLoading = false;
            });
        }
        catch (e) {
            runInAction(() => {
                this._isLoading = false;
            });
        }
    }

    private _ensureValidModel(model: IWallet): IWallet {
        return {
            ...model,
            recharge_amount: toNullableNumber(model.recharge_amount),
            recharge_threshold: toNullableNumber(model.recharge_threshold),
            last_recharge: model.last_recharge ? {
                ...model.last_recharge,
                wallet_after_amount: toNullableNumber(model.last_recharge.wallet_after_amount),
            } : null,
        }
    }

    private _ensureValidWalletSettings(settings: IWalletSettings): IWalletSettings {
        if (isSet(settings.plan)) {
            return {
                ...settings,
                plan: {
                    ...settings.plan!,
                    real_plan_price: toNumber(settings.plan!.real_plan_price),
                },
            }
        }
        
        return {
            ...settings,
        }
    }
}

export default new WalletStore();
